/**
 * Compact match summary shown when a played bracket game is clicked.
 * Lazy-loads the match detail (goalscorers) + shootout (penalties).
 * Deliberately light — score, scorers with photos, penalties. Nothing busy.
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { getToken } from '../lib/auth';
import { badgeUrl, playerUrl, wcPlayerUrl } from '../lib/assetUrls';
import { isWcTeam } from '../lib/wcTeams';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

const GOAL_TYPES = new Set(['goal', 'penalty goal', 'own goal']);

function PlayerPhoto({ playerId, teamId, name }) {
    const [err, setErr] = useState(false);
    const src = isWcTeam(teamId) ? wcPlayerUrl(playerId) : playerUrl(playerId);
    if (err || !playerId) {
        return (
            <span className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-[10px] text-white/60 shrink-0">
                {(name || '?').trim()[0] || '?'}
            </span>
        );
    }
    return <img src={src} alt="" onError={() => setErr(true)} className="w-6 h-6 rounded-full object-cover bg-white/10 shrink-0" />;
}

export default function BracketMatchModal({ matchId, onClose }) {
    const [detail, setDetail] = useState(null);
    const [shootout, setShootout] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        Promise.allSettled([
            axios.get(`${API_URL}/api/football/hub/match/${matchId}`, { headers: authH() }),
            axios.get(`${API_URL}/api/football/hub/match/${matchId}/shootout`, { headers: authH() }),
        ]).then(([d, s]) => {
            if (cancelled) return;
            if (d.status === 'fulfilled') setDetail(d.value.data);
            if (s.status === 'fulfilled' && s.value.data?.available) setShootout(s.value.data);
            setLoading(false);
        });
        return () => { cancelled = true; };
    }, [matchId]);

    const homeId = detail?.home_team_id;
    const awayId = detail?.away_team_id;
    const events = detail?.events || [];
    // A goal counts for the event's team, except own goals which count for the opponent
    const goalsFor = (sideId, otherId) => events
        .filter(e => GOAL_TYPES.has(e.type))
        .filter(e => e.type === 'own goal' ? String(e.team_id) === String(otherId) : String(e.team_id) === String(sideId))
        .sort((a, b) => (a.minute || 0) - (b.minute || 0));
    const homeGoals = goalsFor(homeId, awayId);
    const awayGoals = goalsFor(awayId, homeId);

    const goalLabel = (e) => {
        const min = e.minute ? `${e.minute}${e.minute_extra ? `+${e.minute_extra}` : ''}'` : '';
        const tag = e.type === 'penalty goal' ? ' (P)' : e.type === 'own goal' ? ' (OG)' : '';
        return `${min}${tag}`;
    };

    const GoalRow = ({ e, mirror }) => (
        <div className={`flex items-center gap-1.5 ${mirror ? 'flex-row-reverse text-right' : ''}`}>
            <PlayerPhoto playerId={e.player_id} teamId={e.team_id} name={e.player} />
            <span className="text-xs text-white/90 truncate">{(e.player || '').split(' ').slice(-1)[0]}</span>
            <span className="text-[10px] text-white/40 shrink-0">{goalLabel(e)}</span>
        </div>
    );

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70" onClick={onClose}>
            <div className="w-full max-w-md bg-[#0f1d35] border border-[#1e3a5f] rounded-2xl shadow-2xl overflow-hidden"
                 onClick={e => e.stopPropagation()}>
                {/* Header: badges + score */}
                <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-[#1e3a5f]">
                    <div className="flex flex-col items-center gap-1 flex-1 min-w-0">
                        <img src={badgeUrl(homeId)} alt="" className="w-9 h-9 object-contain" onError={e => { e.currentTarget.style.visibility = 'hidden'; }} />
                        <span className="text-xs text-white/80 truncate max-w-full">{detail?.home_team_name}</span>
                    </div>
                    <div className="text-center shrink-0">
                        <div className="text-2xl font-black text-white tabular-nums">
                            {detail?.home_score ?? '–'} <span className="text-white/40">–</span> {detail?.away_score ?? '–'}
                        </div>
                        {shootout && (
                            <div className="text-[10px] text-amber-400 font-semibold">
                                pens {shootout.home?.penalties_scored}–{shootout.away?.penalties_scored}
                            </div>
                        )}
                        {detail?.status === 'live' && <div className="text-[10px] text-red-400 font-semibold">● {detail?.phase_label || 'LIVE'}</div>}
                    </div>
                    <div className="flex flex-col items-center gap-1 flex-1 min-w-0">
                        <img src={badgeUrl(awayId)} alt="" className="w-9 h-9 object-contain" onError={e => { e.currentTarget.style.visibility = 'hidden'; }} />
                        <span className="text-xs text-white/80 truncate max-w-full">{detail?.away_team_name}</span>
                    </div>
                </div>

                {loading ? (
                    <div className="px-5 py-8 text-center text-white/40 text-sm">Loading…</div>
                ) : (
                    <div className="px-5 py-4 space-y-4">
                        {/* Goalscorers */}
                        {(homeGoals.length > 0 || awayGoals.length > 0) ? (
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1.5">{homeGoals.map((e, i) => <GoalRow key={i} e={e} />)}</div>
                                <div className="space-y-1.5">{awayGoals.map((e, i) => <GoalRow key={i} e={e} mirror />)}</div>
                            </div>
                        ) : (
                            <p className="text-center text-white/40 text-xs">No goals.</p>
                        )}

                        {/* Penalty shootout */}
                        {shootout && (
                            <div className="border-t border-[#1e3a5f] pt-3">
                                <p className="text-center text-[10px] uppercase tracking-wider text-white/50 font-semibold mb-2">Penalty Shootout</p>
                                <div className="grid grid-cols-2 gap-4">
                                    {['home', 'away'].map(sideKey => (
                                        <div key={sideKey} className="space-y-1">
                                            {(shootout[sideKey]?.penalties || []).map((p, i) => (
                                                <div key={i} className={`flex items-center gap-1.5 ${sideKey === 'away' ? 'flex-row-reverse text-right' : ''}`}>
                                                    <span className={`text-xs ${p.outcome === 'scored' ? 'text-green-400' : 'text-red-400'}`}>
                                                        {p.outcome === 'scored' ? '⚽' : p.outcome === 'saved' ? '●' : '✕'}
                                                    </span>
                                                    <span className="text-xs text-white/80 truncate">{(p.name || '').split(' ').slice(-1)[0]}</span>
                                                </div>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <button onClick={onClose} className="w-full py-2.5 text-xs text-white/60 hover:text-white border-t border-[#1e3a5f] transition-colors">
                    Close
                </button>
            </div>
        </div>
    );
}
